<template>
  <div class="cust-nav">
    <div class="cust-nav-logo">
      <router-link :to="{ name: 'Dashboard' }">
        <img src="/pacis-logo.png" alt="Pacis" class="cust-nav-logo-img" />
      </router-link>
    </div>

    <nav class="cust-nav-list">
      <router-link
        v-for="item in items"
        :key="item.text"
        :to="item.link"
        class="cust-nav-item"
        active-class="cust-nav-item--active"
        exact
        :data-customer-tour="tourSlug(item)"
      >
        <v-icon size="20" class="cust-nav-icon">{{ item.icon }}</v-icon>
        <span>{{ item.text }}</span>
        <span
          v-if="item.countKey === 'renewals' && renewalBadge"
          class="cust-nav-count"
          :aria-label="`${renewalCount} up for renewal`"
        >{{ renewalBadge }}</span>
      </router-link>
    </nav>

    <div class="cust-nav-footer">
      <button
        type="button"
        class="cust-tour-trigger"
        title="Take a tour"
        @click="startTour"
      >
        <v-icon size="14" color="rgba(255,255,255,0.5)">mdi-help-circle-outline</v-icon>
        <span>Take a tour</span>
      </button>
      <button type="button" class="cust-nav-logout" @click="signOut">
        <v-icon size="18">mdi-logout</v-icon>
        <span>Log out</span>
      </button>
    </div>
  </div>
</template>

<script>
import cookie from 'vue-cookies';
import { mapGetters } from 'vuex';
import { USER_TYPE } from '@/utils/const';
import { formatBadgeCount } from '@/utils/renewalReminders';

export default {
  name: 'CustomerDrawer',
  computed: {
    ...mapGetters('renewalReminders', { renewalCount: 'count' }),
    renewalBadge() {
      return formatBadgeCount(this.renewalCount);
    },
  },
  data: () => ({
    items: [
      { icon: 'mdi-view-dashboard-outline', text: 'Dashboard', link: { name: 'Dashboard' } },
      { icon: 'mdi-shield-plus-outline', text: 'Products', link: { name: 'CustomerProductSelect' } },
      { icon: 'mdi-shield-car', text: 'My Policies', link: { name: 'MyPolicies' } },
      {
        icon: 'mdi-autorenew', text: 'Renewals', link: { name: 'CustomerRenewals' }, countKey: 'renewals',
      },
      { icon: 'mdi-history', text: 'Activity', link: { name: 'SavedQuotes' } },
      { icon: 'mdi-file-document-edit-outline', text: 'Claims', link: { name: 'Claims' } },
    ],
  }),
  methods: {
    tourSlug(item) {
      return (item?.text || '').toLowerCase().replace(/\s+/g, '-');
    },
    startTour() {
      this.$eventsHub.$emit('customer-tour-open-drawer');
      this.$nextTick(() => {
        setTimeout(() => {
          const tour = this.$tours?.customerDrawerTour;
          if (tour) {
            tour.start();
          } else if (this.$toast && this.$toast.info) {
            this.$toast.info('Tour could not start. Please refresh and try again.', 'Tour');
          }
        }, 150);
      });
    },
    async signOut() {
      cookie.remove(USER_TYPE);
      await this.$store.dispatch('auth/signOut');
      this.$router.push({ name: 'SignIn', query: { portal: 'customer' } });
    },
  },
};
</script>

<style scoped>
.cust-nav {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px 14px;
  background: #184679;
}

.cust-nav-logo {
  padding: 8px 10px 28px;
}

.cust-nav-logo-img {
  height: 36px;
  width: auto;
  filter: brightness(0) invert(1);
}

.cust-nav-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cust-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: background 0.15s;
}

.cust-nav-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.cust-nav-item--active {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.cust-nav-icon {
  color: inherit !important;
}

.cust-nav-count {
  margin-left: auto;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #25D366;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 22px;
  text-align: center;
}

.cust-nav-footer {
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.cust-tour-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 14px;
  margin-bottom: 10px;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.cust-tour-trigger:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.4);
}

.cust-nav-logout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: #fff;
  color: #184679;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
}

.cust-nav-logout:hover {
  background: #f8fafc;
}
</style>
